import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  FileText, Target, BookOpen, MessageCircle,
  CheckCircle, Clock, Briefcase, Star, ArrowRight,
  ChevronRight, AlertCircle, Loader2, Upload, TrendingUp,
  BarChart3, Plus, X
} from "lucide-react";
import axios from "axios";
import {
  price as formatPrice,
  useMarketConfig,
} from "@/utils/marketConfig";
import "@/styles/landing.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const [userData, setUserData] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allAnalyses, setAllAnalyses] = useState([]);
  const [resumeSlots, setResumeSlots] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploadedResume, setUploadedResume] = useState(null);
  const [targetRole, setTargetRole] = useState("");
  const [freeAnalysis, setFreeAnalysis] = useState(null); // { has_free_analysis, granted_at }
  const [claimingFree, setClaimingFree] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const { getHeaders, user } = useAuth();
  const { cfg: market, ready: marketReady } = useMarketConfig();

  useEffect(() => {
    if (!user) { navigate("/auth?return=/dashboard"); return; }
    fetchData();
  }, [user]); // eslint-disable-line

  const fetchData = async () => {
    try {
      const [subRes, analysisRes, analysesRes, slotsRes, freeRes] = await Promise.allSettled([
        axios.get(`${API}/user/subscription`, { headers: getHeaders() }),
        axios.get(`${API}/analysis/latest`, { headers: getHeaders() }),
        axios.get(`${API}/dashboard/analyses`, { headers: getHeaders() }),
        axios.get(`${API}/dashboard/resume-slots`, { headers: getHeaders() }),
        axios.get(`${API}/user/free-analysis-status`, { headers: getHeaders() }),
      ]);
      if (subRes.status === "fulfilled") setUserData(subRes.value.data);
      if (analysisRes.status === "fulfilled") setAnalysis(analysisRes.value.data);
      if (analysesRes.status === "fulfilled") setAllAnalyses(analysesRes.value.data.analyses || []);
      if (slotsRes.status === "fulfilled") setResumeSlots(slotsRes.value.data);
      if (freeRes.status === "fulfilled") setFreeAnalysis(freeRes.value.data);
    } catch (err) {
      // ok
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf') && !file.name.toLowerCase().endsWith('.docx')) {
      toast.error("Only PDF and DOCX files are accepted");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await axios.post(`${API}/resume/upload`, formData, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' }
      });

      setUploadedResume(res.data);
      toast.success(`Resume uploaded: ${file.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleQuickAnalyze = async () => {
    if (!uploadedResume) {
      toast.error("Please upload a resume first");
      return;
    }

    setAnalyzing(true);
    try {
      const res = await axios.post(`${API}/dashboard/quick-analyze`, {
        resume_id: uploadedResume.id,
        target_role: targetRole || userData?.target_role || ""
      }, { headers: getHeaders() });

      if (res.data.is_duplicate) {
        toast.success("Returning resume detected! Your improved score is ready.");
      } else {
        toast.success("Analysis complete! View your ATS score.");
      }

      setShowUploadModal(false);
      setUploadedResume(null);
      setTargetRole("");
      fetchData(); // Refresh all data
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const hasSeasonPass = userData?.has_season_pass;
  const userPlan = userData?.plan;
  // Greeting: first name only — fall back to the email's local part (capitalized),
  // never the full email address.
  const firstName = (() => {
    const n = (user?.name || "").trim().split(/\s+/)[0];
    if (n) return n;
    const local = (user?.email || "").split("@")[0];
    return local ? local.charAt(0).toUpperCase() + local.slice(1) : "there";
  })();
  const hasPaidEntry = !!(userPlan === 'entry' || userPlan === 'session_pass' || hasSeasonPass);

  const claimFreeAnalysis = async () => {
    if (!analysis?.id) { setShowUploadModal(true); return; }
    setClaimingFree(true);
    try {
      const res = await axios.post(`${API}/analysis/${analysis.id}/claim-free`, {}, { headers: getHeaders() });
      setAnalysis(res.data);
      setFreeAnalysis({ has_free_analysis: false });
      toast.success("🎉 Your free ATS-optimised resume is ready!");
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (detail.includes("not found")) {
        // Need to upload resume first
        setShowUploadModal(true);
      } else {
        toast.error(detail || "Failed to claim free analysis");
      }
    } finally {
      setClaimingFree(false);
    }
  };
  const atsScore = analysis?.score || 0;
  // Get improved score from rewritten resume if available
  const improvedScore = analysis?.rewritten_resume?.estimated_new_score || (analysis?.status === "complete" ? Math.min(atsScore + 15, 95) : null);
  const displayScore = improvedScore || atsScore;
  // No analysis yet → no probability. Never fabricate a number (the old `: 35`
  // showed brand-new users a fake "35% · Low" personal readiness score).
  const placementProb = displayScore ? Math.min(displayScore + 10, 95) : null;
  const hasProb = placementProb !== null;
  const probLabel = !hasProb ? "" : placementProb < 50 ? "Low" : placementProb < 75 ? "Moderate" : "High";
  const probTone = placementProb < 50 ? "#dc2626" : placementProb < 75 ? "#d97706" : "#16a34a";
  const probBg = placementProb < 50 ? "#fef2f2" : placementProb < 75 ? "#fffbeb" : "#f0fdf4";

  const currentTargetRole = analysis?.target_role || userData?.target_role || "Not set";
  const resumeStatus = analysis?.status === "complete" ? "Optimized" : "Pending";

  if (loading) {
    return (
      <div className="ap-root min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--ap-violet)" }} />
      </div>
    );
  }

  // Score dots for ATS - use improved score if available
  const scoreDots = [];
  const dotCount = 4;
  const filledDots = Math.round((displayScore / 100) * dotCount);
  for (let i = 0; i < dotCount; i++) {
    if (i < filledDots) {
      scoreDots.push(i < filledDots / 2 ? "#16a34a" : "#22c55e");
    } else {
      scoreDots.push("#e5e7eb");
    }
  }

  const ecosystemItems = [
    { icon: FileText, title: "Resume Vault", desc: "Manage all your resume versions", path: "/templates", tint: "#FFF1E9", strokeColor: "#ea580c", locked: false },
    { icon: Target, title: "Job Feed", desc: "AI-matched job listings for your role", path: "/jobs", tint: "#E8EEFF", strokeColor: "#2563eb", locked: false },
    { icon: BookOpen, title: "Skill Path", desc: "Personalized upskilling recommendations", path: "/skills", tint: "#F1E9FF", strokeColor: "#9333ea", locked: false },
    { icon: MessageCircle, title: "AI Companion", desc: "Career coaching chatbot", path: "/companion", tint: "#E7F8EE", strokeColor: "#16a34a", locked: !hasSeasonPass },
  ];

  const activities = [];
  if (analysis) {
    if (analysis.status === "complete") {
      activities.push({ icon: CheckCircle, title: "Resume analysis completed", desc: "Your resume has been optimized for ATS", time: "Recent", tint: "#E7F8EE", strokeColor: "#16a34a" });
    }
    activities.push({ icon: FileText, title: "Resume uploaded", desc: "Resume file has been received", time: "Recent", tint: "#E8EEFF", strokeColor: "#2563eb" });
    if (currentTargetRole !== "Not set") {
      activities.push({ icon: Target, title: "Target role selected", desc: `${currentTargetRole} profile created`, time: "Recent", tint: "#F1E9FF", strokeColor: "#9333ea" });
    }
  }
  if (activities.length === 0) {
    activities.push({ icon: AlertCircle, title: "No activity yet", desc: "Upload your resume to get started", time: "", tint: "#E8EEFF", strokeColor: "#2563eb" });
  }

  return (
    <div className="ap-root min-h-screen pt-24 pb-16" data-testid="dashboard-page">
      <div className="ap-section flex flex-col gap-6">

        {/* 🎁 Free Analysis Banner */}
        {freeAnalysis?.has_free_analysis && (
          <div
            className="ap-card ap-rise rounded-[20px] p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 overflow-hidden relative"
            style={{ background: "linear-gradient(108deg, #FF6B35, #e55a2b)", border: "none", color: "#fff" }}
          >
            <div className="flex items-start gap-3 relative z-10">
              <span className="text-3xl">🎁</span>
              <div>
                <p className="ap-display font-extrabold text-base text-white">You have a free ATS resume analysis!</p>
                <p className="text-sm text-white/85 mt-0.5">
                  {analysis?.id
                    ? "Click below to generate your ATS-optimised resume — no payment needed."
                    : "Upload your resume first, then claim your free ATS-optimised resume."}
                </p>
              </div>
            </div>
            <button
              onClick={claimFreeAnalysis}
              disabled={claimingFree}
              className="ap-btn ap-btn-md shrink-0 relative z-10 disabled:opacity-60"
              style={{ background: "#fff", color: "#FF6B35" }}
            >
              {claimingFree ? <Loader2 className="w-4 h-4 animate-spin" /> : <span>✨</span>}
              {claimingFree ? "Generating..." : analysis?.id ? "Claim Free Analysis" : "Upload Resume First"}
            </button>
          </div>
        )}

        {/* Header */}
        <div className="ap-mesh rounded-[24px] overflow-hidden relative ap-rise">
          <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-5 p-7 md:p-9">
            <div>
              <span className="ap-eyebrow">Your career command center</span>
              <h1 className="ap-display ap-h2 mt-2" style={{ fontSize: "clamp(28px,4vw,42px)" }}>
                Welcome back, <span className="ap-grad-text">{firstName}</span>
              </h1>
              <p className="text-[var(--ap-muted)] mt-2 max-w-xl">
                Track your placement readiness, fix what's holding you back, and apply to roles you can actually win.
              </p>
            </div>
            <div className="shrink-0">
              <button
                className="ap-btn ap-btn-fire ap-btn-lg"
                onClick={() => (hasSeasonPass ? setShowUploadModal(true) : navigate("/intake"))}
              >
                <Plus className="w-5 h-5" /> Analyse a resume
              </button>
            </div>
          </div>
        </div>

        {/* Placement Probability Card */}
        <div className="ap-card ap-rise ap-rise-1 p-7" data-testid="probability-card">
          {hasProb ? (
            <>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div>
                  <h2 className="ap-display text-[20px] font-extrabold text-[var(--ap-ink)]">Placement Probability</h2>
                  <p className="text-[var(--ap-muted)] text-sm mt-1">
                    {placementProb < 50 ? "Complete more steps to improve your chances" : placementProb < 75 ? "Good progress! Keep going." : "Excellent standing!"}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="ap-display text-[52px] leading-none font-extrabold ap-grad-text">{placementProb}%</span>
                  <span
                    className="ap-chip"
                    style={{ background: probBg, borderColor: "transparent", color: probTone, fontWeight: 700 }}
                  >
                    {probLabel}
                  </span>
                </div>
              </div>
              <div className="mt-5 h-2.5 w-full rounded-full overflow-hidden" style={{ background: "var(--ap-canvas-2)" }}>
                <div
                  className="h-full rounded-full"
                  style={{ width: `${placementProb}%`, background: "var(--ap-fire)", transition: "width .6s cubic-bezier(.2,.8,.2,1)" }}
                />
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4">
                <p className="text-sm text-[var(--ap-muted)] m-0">
                  <strong className="text-[var(--ap-ink)]">Next steps:</strong> Optimize your resume, complete skill assessments, and apply to matched jobs
                </p>
                {analysis?.status === "complete" && (
                  <button
                    onClick={() => navigate("/templates")}
                    className="ap-btn ap-btn-pri ap-btn-md shrink-0"
                  >
                    <FileText className="w-4 h-4" /> View My Resume
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="ap-display text-[20px] font-extrabold text-[var(--ap-ink)]">Placement Probability</h2>
                <p className="text-[var(--ap-muted)] text-sm mt-1">Analyse a resume to unlock your placement readiness — it's free.</p>
              </div>
              <button
                onClick={() => (hasSeasonPass ? setShowUploadModal(true) : navigate("/intake"))}
                className="ap-btn ap-btn-fire ap-btn-md shrink-0"
                data-testid="probability-analyze-btn"
              >
                <Plus className="w-4 h-4" /> Analyse a resume
              </button>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {/* ATS Score - Shows improved score prominently */}
          <div className="ap-card ap-pop ap-rise ap-rise-1 p-5" data-testid="stat-ats">
            <div className="flex items-start justify-between">
              <span className="text-sm font-medium text-[var(--ap-muted)]">{improvedScore ? "Optimized ATS Score" : "ATS Score"}</span>
              <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: improvedScore ? "#E7F8EE" : "#E8EEFF" }}>
                {improvedScore ? <TrendingUp className="w-5 h-5" style={{ color: "#16a34a" }} /> : <BarChart3 className="w-5 h-5" style={{ color: "#2563eb" }} />}
              </div>
            </div>
            <div className="ap-display text-[34px] font-extrabold mt-2" style={{ color: improvedScore ? "#16a34a" : "var(--ap-ink)" }}>
              {displayScore || "--"}
              {improvedScore && atsScore && (
                <span className="text-xs text-gray-400 ml-2 font-normal">(was {atsScore})</span>
              )}
            </div>
            <div className="flex gap-1.5 mt-3">
              {scoreDots.map((c, i) => <span key={i} className="w-7 h-1.5 rounded-full" style={{ background: c }} />)}
            </div>
            {improvedScore && (
              <span className="text-xs text-green-600 font-medium mt-2 inline-block">+{improvedScore - atsScore} improvement</span>
            )}
          </div>

          {/* Target Role */}
          <div className="ap-card ap-pop ap-rise ap-rise-2 p-5" data-testid="stat-role">
            <div className="flex items-start justify-between">
              <span className="text-sm font-medium text-[var(--ap-muted)]">Target Role</span>
              <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: "#F1E9FF" }}>
                <Briefcase className="w-5 h-5" style={{ color: "#9333ea" }} />
              </div>
            </div>
            <div className="ap-display text-[18px] font-bold text-[var(--ap-ink)] mt-2 leading-snug">{currentTargetRole}</div>
            <button className="text-sm font-semibold mt-2 inline-flex items-center gap-1" style={{ color: "var(--ap-violet)" }} onClick={() => navigate("/intake")}>Change role →</button>
          </div>

          {/* Resume Status */}
          <div className="ap-card ap-pop ap-rise ap-rise-3 p-5" data-testid="stat-resume">
            <div className="flex items-start justify-between">
              <span className="text-sm font-medium text-[var(--ap-muted)]">Resume Status</span>
              <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: "#E7F8EE" }}>
                <CheckCircle className="w-5 h-5" style={{ color: "#16a34a" }} />
              </div>
            </div>
            <div className="ap-display text-[20px] font-extrabold mt-2" style={{ color: resumeStatus === "Optimized" ? "#16a34a" : "var(--ap-muted)" }}>{resumeStatus}</div>
            <span className="text-xs text-[var(--ap-muted)] mt-1 inline-block">{analysis ? "Last updated recently" : "No resume yet"}</span>
          </div>

          {/* Full Career OS */}
          <div className="ap-card ap-pop ap-rise ap-rise-4 p-5" data-testid="stat-pass">
            <div className="flex items-start justify-between">
              <span className="text-sm font-medium text-[var(--ap-muted)]">Full Career OS</span>
              <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: hasSeasonPass ? "var(--ap-violet-soft)" : "#F3F4F6" }}>
                {hasSeasonPass
                  ? <Star className="w-5 h-5" style={{ color: "#2832c2" }} />
                  : <AlertCircle className="w-5 h-5" style={{ color: "#6b7280" }} />
                }
              </div>
            </div>
            <div className="ap-display text-[20px] font-extrabold mt-2" style={{ color: hasSeasonPass ? "#16a34a" : "var(--ap-muted)" }}>
              {hasSeasonPass ? "Active" : "Inactive"}
            </div>
            {!hasSeasonPass && <button className="text-sm font-semibold mt-1 inline-flex items-center gap-1" style={{ color: "var(--ap-violet)" }} onClick={() => navigate("/season-pass")}>Upgrade now →</button>}
            {hasSeasonPass && <span className="text-xs text-[var(--ap-muted)] mt-1 inline-block">Full access enabled</span>}
          </div>
        </div>

        {/* Analysis Section — shown for ALL paying users */}
        {(hasSeasonPass || hasPaidEntry) && (
          <div className="ap-card p-7">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
              <div>
                <h2 className="ap-display text-[20px] font-extrabold text-[var(--ap-ink)]">
                  {hasSeasonPass ? "Resume Analysis Hub" : "Your Analysis Report"}
                </h2>
                <p className="text-sm text-[var(--ap-muted)] mt-1">
                  {hasSeasonPass
                    ? `Analyze up to 5 resumes • ${resumeSlots?.used_slots || 0} of ${resumeSlots?.max_slots || 5} slots used`
                    : "Your paid analysis — click to view full report & optimized resume"
                  }
                </p>
              </div>
              {hasSeasonPass ? (
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="ap-btn ap-btn-pri ap-btn-md shrink-0"
                >
                  <Plus className="w-4 h-4" /> Analyze New Resume
                </button>
              ) : hasPaidEntry && (
                <button
                  onClick={() => navigate("/intake")}
                  className="ap-btn ap-btn-ghost ap-btn-md shrink-0"
                  style={{ color: "var(--ap-violet)", borderColor: "var(--ap-violet)" }}
                >
                  <Plus className="w-4 h-4" /> Analyze Another Resume
                </button>
              )}
            </div>

            {/* Analysis History */}
            {allAnalyses.length > 0 && (
              <div className="grid gap-3">
                {allAnalyses.slice(0, 5).map((a, i) => (
                  <div
                    key={a.id}
                    onClick={() => navigate(`/templates`)}
                    className="ap-pop flex items-center justify-between p-4 rounded-2xl cursor-pointer"
                    style={{ border: "1px solid var(--ap-line)", background: "#fff" }}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className="w-10 h-10 rounded-xl grid place-items-center shrink-0"
                        style={{ background: a.status === "complete" ? "#dcfce7" : "#fef3c7" }}
                      >
                        <FileText className="w-5 h-5" style={{ color: a.status === "complete" ? "#16a34a" : "#d97706" }} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[var(--ap-ink)] m-0">
                          {a.filename || "Resume"} {a.is_duplicate && <span style={{ color: "#16a34a", fontSize: "0.75rem" }}>(Returning)</span>}
                        </p>
                        <p className="text-xs text-[var(--ap-muted)] mt-0.5 m-0">
                          {a.target_role || "General"} • {new Date(a.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p
                          className="ap-display text-[20px] font-extrabold m-0"
                          style={{ color: (a.improved_score || a.score) >= 70 ? "#16a34a" : (a.improved_score || a.score) >= 50 ? "#d97706" : "#dc2626" }}
                        >
                          {a.improved_score || a.score}
                        </p>
                        <p className="text-[0.625rem] uppercase tracking-wide m-0" style={{ color: "#9ca3af" }}>
                          {a.status === "complete" ? "Optimized" : "ATS Score"}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5" style={{ color: "#9ca3af" }} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {allAnalyses.length === 0 && (
              <div
                className="p-10 rounded-2xl text-center"
                style={{ border: "2px dashed var(--ap-line)", color: "var(--ap-muted)" }}
              >
                <div className="w-14 h-14 rounded-2xl grid place-items-center mx-auto mb-3" style={{ background: "var(--ap-violet-soft)" }}>
                  <Upload className="w-7 h-7" style={{ color: "var(--ap-violet)" }} />
                </div>
                <p className="m-0 font-medium text-[var(--ap-ink)]">No resumes analyzed yet</p>
                <p className="m-0 text-sm mt-1">Upload your first resume to get started!</p>
              </div>
            )}
          </div>
        )}

        {/* Upgrade Banner — only for users who have never paid */}
        {!hasSeasonPass && !hasPaidEntry && (
          <div
            className="ap-card rounded-[22px] p-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 overflow-hidden relative"
            style={{ background: "var(--ap-fire)", border: "none", color: "#fff" }}
            data-testid="upsell-banner"
          >
            <div className="flex items-start gap-4 relative z-10">
              <div className="w-14 h-14 rounded-2xl grid place-items-center shrink-0" style={{ background: "rgba(255,255,255,.18)" }}>
                <Star className="w-7 h-7" style={{ color: "#fff" }} />
              </div>
              <div>
                <h3 className="ap-display text-[20px] font-extrabold text-white">Upgrade to Full Career OS</h3>
                <p className="text-white/85 text-sm mt-1 max-w-xl">
                  Unlock job matching, skills path, AI companion, and analyze up to 5 resumes for{" "}
                  {marketReady ? formatPrice(market, market.prices.pass_annual) : "…"}
                </p>
              </div>
            </div>
            <button className="ap-btn ap-btn-md shrink-0 relative z-10" style={{ background: "#fff", color: "var(--ap-violet)" }} onClick={() => navigate("/season-pass")}>
              UPGRADE NOW
              <ArrowRight className="w-5 h-5" style={{ color: "var(--ap-violet)" }} />
            </button>
          </div>
        )}

        {/* Soft upgrade nudge for entry plan users (already paid ₹99) */}
        {!hasSeasonPass && hasPaidEntry && (
          <div
            className="ap-card rounded-[22px] p-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 overflow-hidden relative"
            style={{ background: "linear-gradient(135deg, #0f1e45 0%, #1a2847 100%)", border: "1px solid #2832C2", color: "#fff" }}
            data-testid="soft-upgrade-banner"
          >
            <div className="flex items-start gap-4 relative z-10">
              <div className="w-14 h-14 rounded-2xl grid place-items-center shrink-0" style={{ background: "#FF6B35" }}>
                <Star className="w-7 h-7" style={{ color: "#fff" }} />
              </div>
              <div>
                <h3 className="ap-display text-[20px] font-extrabold text-white">Want more from PlacementOS?</h3>
                <p className="text-white/85 text-sm mt-1 max-w-xl">Unlock job matching, AI companion, skills path & multi-resume analysis — upgrade to Full Career OS</p>
              </div>
            </div>
            <button className="ap-btn ap-btn-md shrink-0 relative z-10" style={{ background: "#fff", color: "var(--ap-violet)" }} onClick={() => navigate("/season-pass")} data-testid="entry-upgrade-btn">
              LEARN MORE
              <ArrowRight className="w-5 h-5" style={{ color: "var(--ap-violet)" }} />
            </button>
          </div>
        )}

        {/* Ecosystem Section */}
        <div>
          <h2 className="ap-display text-[22px] font-extrabold text-[var(--ap-ink)] mb-4">Ecosystem</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {ecosystemItems.map((item, i) => (
              <div
                key={i}
                className={`ap-card p-6 relative ${item.locked ? "opacity-90 cursor-default" : "ap-pop cursor-pointer"}`}
                onClick={item.locked ? undefined : () => navigate(item.path)}
                data-testid={`ecosystem-${item.title.toLowerCase().replace(/ /g, '-')}`}
              >
                <div className="flex items-start justify-between">
                  <div className="w-12 h-12 rounded-2xl grid place-items-center" style={{ background: item.tint }}>
                    <item.icon className="w-6 h-6" style={{ color: item.strokeColor }} />
                  </div>
                  <div className="flex items-center gap-2">
                    {item.locked && (
                      <span className="ap-chip" style={{ background: "var(--ap-violet-soft)", borderColor: "transparent", color: "var(--ap-violet)", fontSize: 11, padding: "4px 9px" }}>PREMIUM</span>
                    )}
                    <ChevronRight className="w-5 h-5" style={{ color: "#9ca3af" }} />
                  </div>
                </div>
                <h3 className="ap-display text-[16px] font-bold text-[var(--ap-ink)] mt-4">{item.title}</h3>
                <p className="text-sm text-[var(--ap-muted)] mt-1">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="ap-card p-7">
          <h2 className="ap-display text-[20px] font-extrabold text-[var(--ap-ink)] mb-4">Recent Activity</h2>
          <div className="flex flex-col gap-2">
            {activities.map((a, i) => (
              <div key={i} className="flex items-center gap-4 p-3 rounded-2xl" style={{ background: "var(--ap-canvas)" }}>
                <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0" style={{ background: a.tint }}>
                  <a.icon className="w-6 h-6" style={{ color: a.strokeColor }} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-[var(--ap-ink)] m-0">{a.title}</p>
                  <p className="text-xs text-[var(--ap-muted)] mt-0.5 m-0">{a.desc}</p>
                </div>
                {a.time && <span className="text-xs text-[var(--ap-muted)] shrink-0">{a.time}</span>}
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button className="ap-btn ap-btn-pri ap-btn-md flex-1" onClick={() => navigate("/templates")} data-testid="view-analysis-btn">
            View Full Analysis
          </button>
          <button className="ap-btn ap-btn-ghost ap-btn-md flex-1" onClick={() => navigate("/intake")} data-testid="new-analysis-btn">
            Analyze Another Resume
          </button>
        </div>
      </div>

      {/* Upload Modal for Full Career OS Users */}
      {showUploadModal && (
        <div
          className="fixed inset-0 flex items-center justify-center p-4"
          style={{ background: "rgba(12,17,29,0.55)", zIndex: 50, backdropFilter: "blur(2px)" }}
        >
          <div className="ap-card ap-rise w-full p-6" style={{ maxWidth: "28rem" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="ap-display text-[18px] font-extrabold text-[var(--ap-ink)] m-0">
                Analyze New Resume
              </h3>
              <button
                onClick={() => { setShowUploadModal(false); setUploadedResume(null); setTargetRole(""); }}
                style={{ background: "transparent", border: "none", cursor: "pointer", padding: "0.25rem" }}
              >
                <X className="w-5 h-5" style={{ color: "var(--ap-muted)" }} />
              </button>
            </div>

            {/* File Upload */}
            <div
              onClick={() => fileInputRef.current?.click()}
              className="rounded-2xl p-8 text-center cursor-pointer mb-4 transition-all"
              style={{
                border: uploadedResume ? "2px solid #16a34a" : "2px dashed var(--ap-line)",
                background: uploadedResume ? "#f0fdf4" : "var(--ap-canvas)"
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx"
                onChange={handleFileUpload}
                style={{ display: "none" }}
              />
              {uploading ? (
                <Loader2 className="w-8 h-8 mx-auto animate-spin" style={{ color: "var(--ap-violet)" }} />
              ) : uploadedResume ? (
                <>
                  <CheckCircle className="w-8 h-8 mx-auto" style={{ color: "#16a34a" }} />
                  <p className="mt-2 font-medium" style={{ color: "#16a34a", margin: "0.5rem 0 0 0" }}>
                    {uploadedResume.filename || "Resume uploaded"}
                  </p>
                </>
              ) : (
                <>
                  <Upload className="w-8 h-8 mx-auto" style={{ color: "#9ca3af" }} />
                  <p className="mt-2 text-[var(--ap-muted)]" style={{ margin: "0.5rem 0 0 0" }}>
                    Click to upload PDF or DOCX
                  </p>
                </>
              )}
            </div>

            {/* Target Role Input */}
            <input
              type="text"
              placeholder="Target role (optional)"
              value={targetRole}
              onChange={(e) => setTargetRole(e.target.value)}
              className="w-full mb-4 text-sm"
              style={{
                padding: "0.75rem 1rem", borderRadius: "0.75rem",
                border: "1px solid var(--ap-line)", outline: "none", boxSizing: "border-box"
              }}
              onFocus={(e) => e.target.style.borderColor = "var(--ap-violet)"}
              onBlur={(e) => e.target.style.borderColor = "var(--ap-line)"}
            />

            {/* Info Text */}
            <p className="text-xs text-[var(--ap-muted)] mb-4">
              💡 Re-uploading the same resume after improvements will show a higher ATS score!
            </p>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => { setShowUploadModal(false); setUploadedResume(null); setTargetRole(""); }}
                className="ap-btn ap-btn-ghost ap-btn-md flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleQuickAnalyze}
                disabled={!uploadedResume || analyzing}
                className="ap-btn ap-btn-pri ap-btn-md flex-1"
                style={{
                  background: uploadedResume ? "var(--ap-violet)" : "#e5e7eb",
                  color: uploadedResume ? "#fff" : "#9ca3af",
                  cursor: uploadedResume ? "pointer" : "not-allowed",
                  boxShadow: uploadedResume ? undefined : "none"
                }}
              >
                {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
                {analyzing ? "Analyzing..." : "Analyze Resume"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
